(() => {
  'use strict';

  const formatters = new Map();
  const wallParts = (epoch, timeZone) => {
    if (!formatters.has(timeZone)) {
      formatters.set(timeZone, new Intl.DateTimeFormat('en-GB', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      }));
    }
    return Object.fromEntries(formatters.get(timeZone).formatToParts(new Date(epoch))
      .filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
  };

  const parseDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;
    const [year, month, day] = match.slice(1).map(Number);
    if (year < 2000) return null;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
      ? { year, month, day } : null;
  };

  const parseTime = (value) => {
    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || '');
    if (!match) return null;
    const [hour, minute, second] = [Number(match[1]), Number(match[2]), Number(match[3] || 0)];
    return hour < 24 && minute < 60 && second < 60 ? { hour, minute, second } : null;
  };

  // Convert a Berlin wall-clock deadline to an absolute instant, including DST.
  // No visitor-local date parsing or per-session/evergreen deadline is used.
  const zonedEpoch = (parts, timeZone) => {
    try {
      const wallEpoch = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      let epoch = wallEpoch;
      for (let attempt = 0; attempt < 4; attempt++) {
        const actual = wallParts(epoch, timeZone);
        const actualWall = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
        const correction = wallEpoch - actualWall;
        if (correction === 0) return epoch;
        epoch += correction;
      }
      return NaN; // Invalid/nonexistent local times fail closed.
    } catch {
      return NaN;
    }
  };

  const deadline = (config, now = Date.now()) => {
    const time = parseTime(config.endTime);
    if (!time) return NaN;
    const zone = config.timeZone || 'Europe/Berlin';
    try {
      const date = config.mode === 'daily' ? wallParts(now, zone) : parseDate(config.endDate);
      if (!date || !['fixed', 'daily'].includes(config.mode)) return NaN;
      return zonedEpoch({ ...date, ...time }, zone);
    } catch {
      return NaN;
    }
  };

  const timeText = (milliseconds, locale) => {
    const total = Math.max(0, Math.ceil(milliseconds / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor(total % 86400 / 3600);
    const minutes = Math.floor(total % 3600 / 60);
    const seconds = total % 60;
    const clock = [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':');
    return (days ? `${days} ${locale === 'it' ? 'g' : 'T'} ` : '') + clock;
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseDate, parseTime, wallParts, zonedEpoch, deadline, timeText };
  }
  if (typeof customElements === 'undefined' || customElements.get('af-familienfest-countdown')) return;

  customElements.define('af-familienfest-countdown', class extends HTMLElement {
    static get observedAttributes() {
      return ['data-mode', 'data-end-date', 'data-end-time', 'data-time-zone', 'data-locale'];
    }

    connectedCallback() {
      this.start();
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      this.interval = undefined;
    }

    attributeChangedCallback() {
      if (this.isConnected) this.start();
    }

    start() {
      clearInterval(this.interval);
      this.interval = undefined;
      this.valueElement = this.querySelector('[data-af-countdown-value]');
      this.hidden = true;
      if (!this.valueElement) return;
      this.update();
      // A daily timer stays hidden after its deadline until the next Berlin day.
      // It never immediately restarts for another 24 hours at expiration.
      if (this.dataset.mode === 'daily' || !this.hidden) {
        this.interval = setInterval(() => this.update(), 1000);
      }
    }

    update() {
      const now = Date.now();
      const end = deadline({ mode: this.dataset.mode, endDate: this.dataset.endDate,
        endTime: this.dataset.endTime, timeZone: this.dataset.timeZone }, now);
      if (!Number.isFinite(end) || end <= now) {
        this.hidden = true;
        if (this.dataset.mode !== 'daily') {
          clearInterval(this.interval);
          this.interval = undefined;
        }
        return;
      }
      this.valueElement.textContent = timeText(end - now, this.dataset.locale);
      if (this.lastDeadline !== end || this.lastLocale !== this.dataset.locale) {
        const italian = this.dataset.locale === 'it';
        const readable = new Intl.DateTimeFormat(italian ? 'it-IT' : 'de-DE', {
          timeZone: this.dataset.timeZone || 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short',
        }).format(new Date(end));
        const label = (italian ? 'La promozione Festa in famiglia termina il ' : 'Familienfest-Angebot endet am ') + readable;
        this.setAttribute('aria-label', label);
        this.title = label;
        this.lastDeadline = end;
        this.lastLocale = this.dataset.locale;
      }
      this.hidden = false;
    }
  });
})();
